import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TypedFormControl, TypedFormGroup } from '@principle-theorem/ng-shared';
import {
  IBookingWindowTiming,
  OnlineBookingTimingUnit,
} from '@principle-theorem/principle-core/interfaces';
import {
  getEnumValues,
  MAX_BOOKING_WINDOW_DAYS,
} from '@principle-theorem/shared';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

function maxBookingWindowValidator(): ValidatorFn {
  return (control) => {
    const value = control.value as IBookingWindowTiming;
    if (!value?.amount || !value?.unit) {
      // eslint-disable-next-line no-null/no-null
      return null;
    }

    let days = 0;
    switch (value.unit) {
      case OnlineBookingTimingUnit.Weeks:
        days = value.amount * 7;
        break;
      case OnlineBookingTimingUnit.Months:
        days = value.amount * 30;
        break;
      default:
        days = value.amount;
        break;
    }

    if (days > MAX_BOOKING_WINDOW_DAYS) {
      return {
        maxBookingWindow: { max: MAX_BOOKING_WINDOW_DAYS, actual: days },
      };
    }

    // eslint-disable-next-line no-null/no-null
    return null;
  };
}

export class TimingFormGroup extends TypedFormGroup<IBookingWindowTiming> {
  constructor() {
    super(
      {
        amount: new TypedFormControl<number | undefined>(
          undefined,
          Validators.min(1)
        ),
        unit: new TypedFormControl<OnlineBookingTimingUnit | undefined>(
          undefined
        ),
      },
      { validators: maxBookingWindowValidator() }
    );
  }
}

@Component({
  selector: 'pr-online-booking-time-restriction-form',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatFormFieldModule,
    MatTooltipModule,
  ],
  templateUrl: './online-booking-time-restriction-form.component.html',
  styleUrl: './online-booking-time-restriction-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnlineBookingTimeRestrictionFormComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  timingUnits = getEnumValues(OnlineBookingTimingUnit);
  maxBookingWindowDays = MAX_BOOKING_WINDOW_DAYS;
  form = new TimingFormGroup();

  @Input({ required: true }) label: string;
  @Input({ required: true }) hint: string;
  @Input() defaultPlaceholder = 'No restriction';
  @Output() bookingWindowChange = new EventEmitter<IBookingWindowTiming>();

  @Input()
  set value(value: IBookingWindowTiming) {
    if (value) {
      this.form.setValue(value, { emitEvent: false });
    }
  }

  constructor() {
    this.form.valueChanges
      .pipe(debounceTime(1000), takeUntil(this._onDestroy$))
      .subscribe((value) => {
        if (value.amount && value.unit && this.form.valid) {
          this.bookingWindowChange.emit(value);
        }
      });
  }

  clearForm(): void {
    this.form.reset();
    this.bookingWindowChange.emit(undefined);
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }
}
